import { Fragment, useEffect, useRef } from 'react'

import { TopicId } from '~/dashboard/utils/learningPath.utils'
import { useAppDispatch } from '~/redux/hooks'
import { addActiveCompetency, removeActiveCompetency } from '~/curriculum/slice/persistCurriculum.slice'

import { CurriculumCompetencyDataType } from '../types/curriculum.types'

import CompetencyDescription from './CompetencyDescription'
import CurriculumGradeLevel from './CurriculumGradeLevel'

interface Props {
  topicId: TopicId
  competency: CurriculumCompetencyDataType
  competencyKey: string
  expandLevels: boolean
  uniqueId: string
  shouldScroll?: boolean
  masteryTrackApplicable: boolean
}

export default function CurriculumCompetency({
  topicId,
  competency,
  competencyKey,
  expandLevels = false,
  uniqueId,
  shouldScroll = false,
  masteryTrackApplicable,
}: Props): React.ReactElement {
  const { description, levels, competencyGoalCode, subdivisionOrder } = competency
  const dispatch = useAppDispatch()
  let currentId = competencyKey
  const competencyRef = useRef<HTMLDivElement>(null)

  const onCompetencyClick = (): void => {
    if (expandLevels) {
      dispatch(removeActiveCompetency(uniqueId))
    } else {
      dispatch(addActiveCompetency(uniqueId))
    }
  }

  const isCompetencyCompleted = levels?.every((level) => level?.isCompleted)

  useEffect(() => {
    if (shouldScroll && competencyRef.current) {
      competencyRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [shouldScroll])

  return (
    <div className="relative w-80 h-full" ref={competencyRef}>
      <CompetencyDescription
        code={competencyGoalCode}
        description={description}
        id={currentId}
        isCompleted={isCompetencyCompleted}
        isExpanded={expandLevels}
        masteryTrackApplicable={masteryTrackApplicable}
        onClick={onCompetencyClick}
        topicId={topicId}
      />
      <div
        className={`relative flex flex-col items-center transition-all duration-300 origin-top ${
          expandLevels ? 'scale-y-100 max-h-800' : 'scale-y-0 max-h-0' // max-h is needed for a smooth transition, the data is dynamic so I set it to a high value
        }`}
      >
        {levels.map((level, index) => {
          currentId = `${index}_level_${competencyKey}`
          return (
            <Fragment key={currentId}>
              <span className={`border-l border-2 border-white ${index === 0 ? 'h-6' : 'h-4'}`} />

              <CurriculumGradeLevel
                curriculumLevel={level}
                id={currentId}
                quarterNo={subdivisionOrder}
                topic={topicId}
              />
            </Fragment>
          )
        })}
      </div>
    </div>
  )
}
